import React, { FC, useCallback, useState } from "react";
import { Dialog, ButtonProps, Input } from "@xapp/components";

import { BasicIntegrationCard, IntegrationCardProp, IntegrationConnectionProps } from "./BasicIntegrationCard";

export interface HubspotConnectionProps extends IntegrationConnectionProps {
    integration: "hubspot";
    accessToken: string;
    meetingLinkSlug?: string;
}

export interface HubspotIntegrationCardProps extends IntegrationCardProp {
    accessToken?: string;
    meetingLink?: string;
    /**
     * Connect HubSpot
     */
    onConnect?(props: HubspotConnectionProps): void;
}

/**
 * The meeting-link slug from whatever the customer pasted.
 *
 * The API stores the path of the rep's meeting link (`jordan-sample/30min`), but a customer copies
 * the whole link out of HubSpot (`https://meetings.hubspot.com/jordan-sample/30min`) -- and the API
 * rejects a whole URL, so without this every paste would fail. Only a HubSpot host is unwrapped;
 * anything else is passed through for the API to reject with its own message, rather than the card
 * guessing which part of an arbitrary URL was meant.
 */
export function toMeetingLinkSlug(input: string): string | undefined {
    const trimmed = (input || "").trim();
    if (!trimmed) {
        return undefined;
    }

    const withScheme = /^[a-z]+:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
        const url = new URL(withScheme);
        if (/(^|\.)hubspot\.com$/i.test(url.hostname)) {
            const path = url.pathname.replace(/^\/+|\/+$/g, "");
            return path || undefined;
        }
    } catch {
        // Not a URL at all -- a bare slug, which is what we want anyway.
    }

    return trimmed;
}

export const HubspotIntegrationCard: FC<HubspotIntegrationCardProps> = (props) => {
    const { checked, disabled, isLinked, isActive, onDisconnect, onConnect } = props;

    const [accessToken, setAccessToken] = useState<string>(props.accessToken || "");
    const [meetingLink, setMeetingLink] = useState<string>(props.meetingLink || "");
    const [showDialog, setShowDialog] = useState<boolean>(false);

    const handleHubspotClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
        e.preventDefault();

        if (typeof checked === "boolean") {
            if (typeof props.onClick === "function") {
                props.onClick(e);
            }
        } else {
            // pop up modal
            setShowDialog(true);
        }
    };

    const handleConnectHubspot = useCallback(() => {
        if (typeof onConnect === "function") {
            onConnect({
                integration: "hubspot",
                accessToken: accessToken.trim(),
                meetingLinkSlug: toMeetingLinkSlug(meetingLink),
            });
        }
        setShowDialog(false);
    }, [onConnect, accessToken, meetingLink]);

    const handleDisconnectHubspot = useCallback(() => {
        if (typeof onDisconnect === "function") {
            onDisconnect();
            setShowDialog(false);
        }
    }, [onDisconnect]);

    const actions: ButtonProps[] = [
        {
            label: "Close",
            variation: "secondary",
            onClick: () => {
                setShowDialog(false);
            },
        },
    ];

    if (typeof onConnect === "function") {
        actions.push({
            label: "Connect",
            disabled: !accessToken.trim(),
            onClick: handleConnectHubspot,
        });
    }

    return (
        <>
            <BasicIntegrationCard
                checked={checked}
                disabled={disabled}
                imageUrl="/img/integrations/hubspot/hubspot-logo.svg"
                text="HubSpot"
                badge="TEST"
                href={() => "/integrations/hubspot"}
                onClick={handleHubspotClick}
                isLinked={isLinked}
                isActive={isActive}
                onDisconnect={typeof onDisconnect === "function" ? handleDisconnectHubspot : undefined}
            />
            <Dialog
                active={showDialog}
                actions={actions}
                title="HubSpot Setup"
                onClose={() => {
                    setShowDialog(false);
                }}
            >
                <div style={{ display: "flex", flexDirection: "column", rowGap: "10px" }}>
                    <div>
                        <p>Send captured leads to your HubSpot CRM as contacts.</p>
                        <p>
                            In HubSpot, go to <strong>Settings &gt; Integrations &gt; Private Apps</strong>, create an
                            app with the <code>crm.objects.contacts.write</code> and{" "}
                            <code>crm.objects.contacts.read</code> scopes, and paste its access token below.{" "}
                            <a
                                href="https://developers.hubspot.com/docs/api/private-apps"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                How to create a private app
                            </a>
                        </p>
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Private App Access Token"
                            value={accessToken}
                            type="password"
                            onChange={(value) => setAccessToken(String(value))}
                        />
                    </div>
                    <div style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <Input
                            label="Meeting Link (Optional)"
                            value={meetingLink}
                            onChange={(value) => setMeetingLink(String(value))}
                            placeholder="e.g. https://meetings.hubspot.com/your-name/30min"
                        />
                        <p style={{ fontSize: "0.85em", opacity: 0.75 }}>
                            Add a rep&apos;s meeting link to let customers book on their calendar. Leave it blank to
                            send leads only.
                        </p>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
